// CommonJS with relative requires only, so node tests can require it directly.
const { isPaidSubscription } = require("../../standard-elements/upsell-element/resolve-viewer-subscription");

const PLAN_SHEET_INJECTION_KEY = "planSheet";
const SUBSCRIPTION_PLANS_FEED_FIELD = "subscriptionPlans";
const SUBSCRIPTION_PLAN_CONTEXT = "subscriptionPlan";

const PROVIDERS = Object.freeze({ STRIPE: "stripe", APPLE: "apple", GOOGLE: "google" });
const INTERVALS = Object.freeze({ MONTHLY: "monthly", ANNUALLY: "annually" });
const CHECKOUT_MODES = Object.freeze({ MEMBER_CHOOSES: "member_chooses", APP_STORE: "app_store", WEBSITE: "website" });
const INTERVAL_UNITS = Object.freeze({ daily: "day", weekly: "week", monthly: "month", annually: "year" });
const FALLBACK_INTERVAL_UNIT = "billing period";
const SUBSCRIBED_ELSEWHERE_MESSAGES = Object.freeze({
  stripe: "You're subscribed with a card",
  apple: "You're subscribed through the App Store",
  google: "You're subscribed through Google Play",
});
const FALLBACK_SUBSCRIBED_ELSEWHERE_MESSAGE = "You're already subscribed";
const SHEET_CANVAS_HINT = "Shown when a member taps Subscribe";
const MONTHS_PER_YEAR = 12;

const AUTO_RENEW_TEXT = "Subscriptions renew automatically until cancelled.";
const LEGAL_LINKS = Object.freeze([
  { key: "terms", label: "Terms", path: "/terms-of-service" },
  { key: "privacy", label: "Privacy", path: "/privacy-policy" },
]);

// Theme tokens (no author option) marking the picked plan card; iRow/iColumn resolve them.
const SELECTED_PLAN_BORDER_SIDE = Object.freeze({ color: "striking", width: "thick", style: "solid" });
const SELECT_KEYS = ["Enter", " "];

// The tier is the plan; its intervals are its prices (planPrices, card rows by duration).
const INTERVAL_DURATIONS = Object.freeze({ monthly: "month", annually: "year" });
const REMOVED_PRICE_STATE = "removed";
const TIER_INTERVAL_WORDS = /\b(monthly|month|annually|annual|yearly|year)\b/g;

function normaliseInterval(value) {
  return value === INTERVALS.ANNUALLY ? INTERVALS.ANNUALLY : INTERVALS.MONTHLY;
}

function readPlanId(plan) {
  return Number(plan?.id) || 0;
}

function findTierPrice(plan, interval) {
  const prices = Array.isArray(plan?.planPrices) ? plan.planPrices : [];
  if (prices.length === 0) {
    if (plan?.billingFrequency !== interval) {
      return null;
    }
    return { priceCents: Math.round(Number(plan.price) * 100), currency: plan.currencyCode, subscriptionPlanPriceId: 0 };
  }
  const row = prices.find(price => price.provider === PROVIDERS.STRIPE && price.state !== REMOVED_PRICE_STATE
    && !price.parent_subscription_plan_price_id && price.duration === INTERVAL_DURATIONS[interval]);
  return row ? { priceCents: row.price_cents, currency: row.currency, subscriptionPlanPriceId: row.subscription_plan_price_id } : null;
}

function sellsBothIntervals(plan) {
  return Boolean(findTierPrice(plan, INTERVALS.MONTHLY) && findTierPrice(plan, INTERVALS.ANNUALLY));
}

// Tiers pair by their own prices once any plan sells both; until then, separate plans by name.
function pairsByTierPrices(plans) {
  return plans.some(sellsBothIntervals);
}

function resolveTierKey(plan) {
  return String(plan?.name ?? "").toLowerCase().replace(TIER_INTERVAL_WORDS, " ").replace(/\s+/g, " ").trim();
}

function filterPlansOfInterval(plans, interval) {
  return plans.filter(plan => plan.billingFrequency === interval);
}

function findTierPlan(plans, plan, interval) {
  if (pairsByTierPrices(plans)) {
    return findTierPrice(plan, interval) ? plan : null;
  }
  const tier = resolveTierKey(plan);
  return filterPlansOfInterval(plans, interval).find(candidate => resolveTierKey(candidate) === tier) ?? null;
}

function hasSwitchableIntervals(plans) {
  if (pairsByTierPrices(plans)) {
    return true;
  }
  const monthlyTiers = new Set(filterPlansOfInterval(plans, INTERVALS.MONTHLY).map(resolveTierKey));
  return filterPlansOfInterval(plans, INTERVALS.ANNUALLY).some(plan => monthlyTiers.has(resolveTierKey(plan)));
}

function filterVisiblePlans(plans, interval) {
  if (!hasSwitchableIntervals(plans)) {
    return [...plans];
  }
  const shown = normaliseInterval(interval);
  if (pairsByTierPrices(plans)) {
    return plans.filter(plan => findTierPrice(plan, shown));
  }
  return filterPlansOfInterval(plans, shown);
}

function resolveSelectedPlan(plans, interval, pickedPlanId) {
  const candidates = filterVisiblePlans(plans, interval);
  const findById = id => candidates.find(plan => readPlanId(plan) === id);
  const picked = plans.find(plan => readPlanId(plan) === pickedPlanId);
  const pickedTier = picked ? findTierPlan(candidates, picked, normaliseInterval(interval)) : null;
  return findById(pickedPlanId) ?? pickedTier ?? candidates[0] ?? null;
}

// The sheet is an overlay mobile opens from Subscribe; outside the canvas it never renders inline.
function rendersSheetInline(editMode) {
  return Boolean(editMode);
}

// Only "member chooses" offers a provider pick; the forced modes hide the toggle.
function showsProviderToggle(checkoutMode) {
  return (checkoutMode || CHECKOUT_MODES.MEMBER_CHOOSES) === CHECKOUT_MODES.MEMBER_CHOOSES;
}

// Plans come from the sheet's plans feed; preselection + checkout mode from the sheet's options, all read live.
function createPlanSheetScope(readCheckoutMode = () => CHECKOUT_MODES.MEMBER_CHOOSES) {
  return {
    provider: PROVIDERS.STRIPE,
    interval: INTERVALS.MONTHLY,
    pickedPlanId: 0,
    planSources: [],
    get plans() {
      return this.planSources.flatMap(readPlans => readPlans());
    },
    get selectedPlan() {
      return resolveSelectedPlan(this.plans, this.interval, this.pickedPlanId);
    },
    get selectedPlanId() {
      return readPlanId(this.selectedPlan);
    },
    get showsProviderToggle() {
      return showsProviderToggle(readCheckoutMode());
    },
    addPlanSource(readPlans) {
      this.planSources.push(readPlans);
    },
    removePlanSource(readPlans) {
      this.planSources = this.planSources.filter(source => source !== readPlans);
    },
    setInterval(interval) {
      this.interval = normaliseInterval(interval);
    },
    selectPlan(planId) {
      this.pickedPlanId = Number(planId) || 0;
    },
  };
}

function billingFrequencyUnit(billingFrequency) {
  const unit = INTERVAL_UNITS[billingFrequency];
  if (!unit) {
    throw new Error(
      `Billing Frequency element: unknown billingFrequency ${JSON.stringify(billingFrequency)} on the bound`
      + ` SubscriptionPlan; expected one of ${Object.keys(INTERVAL_UNITS).join(", ")}`
      + " (creator SubscriptionPlanDataModel::VALID_BILLING_FREQUENCIES).",
    );
  }
  return unit;
}

function formatPrice(amount, currencyCode) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: currencyCode || "USD" }).format(Number(amount) || 0);
}

function resolvePlanInterval(plan, interval) {
  return interval && findTierPrice(plan, interval) ? interval : plan.billingFrequency;
}

function formatLabelTemplate(template, plan, interval = "") {
  const shownInterval = resolvePlanInterval(plan, interval);
  const price = findTierPrice(plan, shownInterval);
  const values = {
    price: price ? formatPrice(price.priceCents / 100, price.currency) : formatPrice(plan.price, plan.currencyCode),
    interval: INTERVAL_UNITS[shownInterval] ?? FALLBACK_INTERVAL_UNIT,
    tier: String(plan.name),
  };
  return String(template).replace(/\{(price|interval|tier)\}/g, (match, token) => values[token]);
}

// floor((1 − low/high) × 100) in integer cents: the float form floors 20% to 19.
function computeSavingPercent(lowCents, highCents) {
  if (!(lowCents > 0) || !(highCents > lowCents)) {
    return 0;
  }
  return Math.floor((highCents - lowCents) * 100 / highCents);
}

// The selected tier's annual price vs 12 × its monthly price; 0 when it lacks either or saves nothing.
function computeAnnualSavingPercent(plans, selectedPlan) {
  const findPrice = interval => {
    const plan = selectedPlan ? findTierPlan(plans, selectedPlan, interval) : null;
    return plan ? findTierPrice(plan, interval) : null;
  };
  const monthly = findPrice(INTERVALS.MONTHLY);
  const annual = findPrice(INTERVALS.ANNUALLY);
  if (!monthly || !annual) {
    return 0;
  }
  return computeSavingPercent(annual.priceCents, monthly.priceCents * MONTHS_PER_YEAR);
}

// The session subscription the page payload carries (Subscription::getSessionSubscriptionData).
function readSessionSubscription() {
  const data = typeof window === "undefined" ? {} : window.blclient?.data ?? {};
  return {
    subscriberData: data.subscriberData ?? null,
    subscriptionPlanData: data.subscriptionPlanData ?? null,
    manageUrl: data.subscriptionManageUrl ?? "",
  };
}

function describeOtherProviderSubscription(session, currentProvider) {
  if (!isPaidSubscription(session.subscriberData, session.subscriptionPlanData)) {
    return "";
  }
  const provider = session.subscriberData.provider || PROVIDERS.STRIPE;
  if (provider === currentProvider) {
    return "";
  }
  return SUBSCRIBED_ELSEWHERE_MESSAGES[provider] ?? FALLBACK_SUBSCRIBED_ELSEWHERE_MESSAGE;
}

// A store subscription is managed at the provider's URL; a card one on the site's own page.
function resolveManageSubscriptionLink(session, siteBase, managementPageSlug) {
  if (session.manageUrl) {
    return { href: session.manageUrl, external: true };
  }
  return { href: `${String(siteBase).replace(/\/+$/, "")}/${managementPageSlug}`, external: false };
}

function buildPlanRowBindings(isSelected, selectPlan) {
  const bindings = {
    "role": "button",
    "tabindex": "0",
    "aria-pressed": isSelected ? "true" : "false",
    "onClick": selectPlan,
    "onKeydown": event => {
      if (SELECT_KEYS.includes(event.key)) {
        event.preventDefault();
        selectPlan();
      }
    },
  };
  if (!isSelected) {
    return bindings;
  }
  return {
    ...bindings,
    borderTop: SELECTED_PLAN_BORDER_SIDE,
    borderRight: SELECTED_PLAN_BORDER_SIDE,
    borderBottom: SELECTED_PLAN_BORDER_SIDE,
    borderLeft: SELECTED_PLAN_BORDER_SIDE,
  };
}

function buildLegalLinks(siteBase) {
  const base = String(siteBase).replace(/\/+$/, "");
  return LEGAL_LINKS.map(link => ({ key: link.key, label: link.label, href: `${base}${link.path}` }));
}

module.exports = {
  PLAN_SHEET_INJECTION_KEY,
  SUBSCRIPTION_PLANS_FEED_FIELD,
  SUBSCRIPTION_PLAN_CONTEXT,
  PROVIDERS,
  INTERVALS,
  CHECKOUT_MODES,
  AUTO_RENEW_TEXT,
  SHEET_CANVAS_HINT,
  rendersSheetInline,
  hasSwitchableIntervals,
  filterVisiblePlans,
  findTierPrice,
  resolvePlanInterval,
  createPlanSheetScope,
  formatLabelTemplate,
  billingFrequencyUnit,
  computeAnnualSavingPercent,
  readSessionSubscription,
  describeOtherProviderSubscription,
  resolveManageSubscriptionLink,
  buildPlanRowBindings,
  buildLegalLinks,
};
